import React from "react";
import { useNavigate } from "react-router-dom";
import { Breadcrumb, Button, Card, Form, Table, Badge } from "components/ui/BootstrapCompat";
import { useQuery } from "react-query";
import PopUpWithdrawRequest from "components/popup/PopUpWithdrawRequest";

type Props = {};

export default function PaymentPage({}: Props) {
  const navigate = useNavigate();
  const [popup, setPopup] = React.useState<any>({});
  const [searchTerm, setSearchTerm] = React.useState("");
  const [showWithdrawPopup, setShowWithdrawPopup] = React.useState(false);

  // get data
  const { data, isRefetching, refetch } = useQuery(
    ["payment-list"],
    async () => {
      // call api get payment methods
      // const response = await paymentGetMany({
      //   findby: {},
      //   token: "dummy-token",
      //   limit: 10,
      // });
      // dummy data for now
      return [
        {
          _id: "PAY001",
          name: "BCEL One",
          type: "mobile_banking",
          provider: "BCEL",
          apiKey: "bcel_live_xxxxxxxxxxxx",
          merchantId: "MERCHANT_001",
          status: "active",
          transactionCount: 1250,
          totalAmount: 250000000,
          feePercent: 1.5,
          createdAt: "2024-01-05",
          lastUsed: "2024-01-19 10:30:00",
        },
        {
          _id: "PAY002",
          name: "LDB Trust",
          type: "mobile_banking",
          provider: "LDB",
          apiKey: "ldb_live_xxxxxxxxxxxx",
          merchantId: "MERCHANT_002",
          status: "active",
          transactionCount: 890,
          totalAmount: 178000000,
          feePercent: 1.8,
          createdAt: "2024-01-05",
          lastUsed: "2024-01-19 09:15:00",
        },
        {
          _id: "PAY003",
          name: "OnePay",
          type: "e_wallet",
          provider: "OnePay",
          apiKey: "onepay_live_xxxxxxxxxxxx",
          merchantId: "MERCHANT_003",
          status: "active",
          transactionCount: 2100,
          totalAmount: 315000000,
          feePercent: 2.0,
          createdAt: "2024-01-05",
          lastUsed: "2024-01-19 11:45:00",
        },
        {
          _id: "PAY004",
          name: "Bank Transfer",
          type: "bank_transfer",
          provider: "Manual",
          apiKey: null,
          merchantId: null,
          status: "active",
          transactionCount: 450,
          totalAmount: 900000000,
          feePercent: 0,
          createdAt: "2024-01-05",
          lastUsed: "2024-01-18 16:20:00",
        },
        {
          _id: "PAY005",
          name: "JDB Pay (ປິດໃຊ້ງານ)",
          type: "e_wallet",
          provider: "JDB",
          apiKey: "jdb_test_xxxxxxxxxxxx",
          merchantId: "MERCHANT_005",
          status: "inactive",
          transactionCount: 0,
          totalAmount: 0,
          feePercent: 2.5,
          createdAt: "2024-01-10",
          lastUsed: null,
        },
      ];
    },
  );

  const filteredData = data?.filter((payment: any) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      payment?.name?.toLowerCase().includes(search) ||
      payment?.provider?.toLowerCase().includes(search) ||
      payment?.type?.toLowerCase().includes(search) ||
      payment?._id?.toLowerCase().includes(search)
    );
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("lo-LA").format(amount);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "active":
        return <Badge variant="success">ເປີດໃຊ້ງານ</Badge>;
      case "inactive":
        return <Badge variant="danger">ປິດໃຊ້ງານ</Badge>;
      case "maintenance":
        return <Badge variant="warning">ບຳລຸງຮັກສາ</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case "mobile_banking":
        return "Mobile Banking";
      case "e_wallet":
        return "E-Wallet";
      case "bank_transfer":
        return "ໂອນເງິນ";
      case "qr_code":
        return "QR Code";
      default:
        return type;
    }
  };

  const totalTransactions = data?.reduce(
    (sum: number, p: any) => sum + (p.transactionCount || 0),
    0,
  );
  const totalRevenue = data?.reduce(
    (sum: number, p: any) => sum + (p.totalAmount || 0),
    0,
  );
  const activePayments = data?.filter((p: any) => p.status === "active").length;

  return (
    <div className="min-w-0">
      <Breadcrumb>
        <Breadcrumb.Item active>ການຕັ້ງຄ່າ</Breadcrumb.Item>
        <Breadcrumb.Item active>ຕົວຊຳລະເງິນ</Breadcrumb.Item>
      </Breadcrumb>

      <div className="mb-2 grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4">
        <Card>
          <Card.Header>ຕົວຊຳລະເງິນທັງໝົດ</Card.Header>
          <Card.Body>
            <div>
              <h1 className="break-words text-[28px] leading-tight sm:text-[36px] xl:text-[44px]">
                {data?.length || 0}
                <span className="text-[14px]">ຕົວ</span>
              </h1>
              <div className="text-gray-600 text-[12px]">
                ເປີດໃຊ້ງານ{" "}
                <span className="text-success">{activePayments || 0}</span> ຕົວ
              </div>
            </div>
          </Card.Body>
        </Card>

        <Card>
          <Card.Header>ທຸລະກຳທັງໝົດ</Card.Header>
          <Card.Body>
            <div>
              <h1 className="break-words text-[28px] leading-tight sm:text-[36px] xl:text-[44px]">
                {formatCurrency(totalTransactions || 0)}
                <span className="text-[14px]">ລາຍການ</span>
              </h1>
              <div className="text-gray-600 text-[12px]">
                ທຸລະກຳທີ່ສຳເລັດແລ້ວ
              </div>
            </div>
          </Card.Body>
        </Card>

        <Card>
          <Card.Header>ຍອດລວມທັງໝົດ</Card.Header>
          <Card.Body>
            <div>
              <h1 className="break-words text-[28px] leading-tight sm:text-[36px] xl:text-[44px]">
                {formatCurrency(Math.floor((totalRevenue || 0) / 1000))}
                <span className="text-[14px]">ກີບ</span>
              </h1>
              <div className="text-gray-600 text-[12px]">ລາຍຮັບທັງໝົດ</div>
            </div>
          </Card.Body>
        </Card>

        <Card>
          <Card.Header>ຄ່າທຳນຽມສະເລ່ຍ</Card.Header>
          <Card.Body>
            <div>
              <h1 className="break-words text-[28px] leading-tight sm:text-[36px] xl:text-[44px]">
                {data && data.length > 0
                  ? (
                      data.reduce(
                        (sum: number, p: any) => sum + (p.feePercent || 0),
                        0,
                      ) / data.length
                    ).toFixed(2)
                  : "0.00"}
                <span className="text-[14px]">%</span>
              </h1>
              <div className="text-gray-600 text-[12px]">ຄ່າທຳນຽມສະເລ່ຍ</div>
            </div>
          </Card.Body>
        </Card>
      </div>

      <div className="mb-2 flex flex-wrap gap-2 rounded bg-white p-2">
        <Form.Control
          type="text"
          placeholder="ຄົ້ນຫາ..."
          className="min-w-0 flex-[1_1_100%] border border-primary md:flex-none md:w-[200px]"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <Button variant="primary" className="flex flex-1 items-center sm:flex-none">
          <span className="material-icons">search</span>
        </Button>
        <Button
          variant="outline-primary"
          className="flex-1 sm:flex-none"
          onClick={() => refetch()}
        >
          Refetch
        </Button>
        <Button
          variant="outline-success"
          className="flex-[1_1_100%] sm:flex-none"
          onClick={() => navigate("/withdraw") }
        >
          ໄປຫນ້າແຈ້ງຖອນ
        </Button>
        <div className="hidden flex-1 md:block" />
        <Button
          variant="outline-primary"
          className="flex-[1_1_100%] sm:flex-none"
          onClick={() => setPopup({ PopUpCreatePayment: true })}
        >
          ເພີ່ມຕົວຊຳລະເງິນ
        </Button>
      </div>

      <div>
        <Card border="primary">
          <Card.Header className="p-1">ລາຍການຕົວຊຳລະເງິນ</Card.Header>
          <Table striped bordered hover size="sm" className="min-w-[600px]">
            <thead>
              <tr>
                <th className="py-2 px-3 text-left border-b w-[60px]">ລ/ດ</th>
                <th className="py-2 px-3 text-left border-b">ຊື່</th>
                <th className="py-2 px-3 text-left border-b">ປະເພດ</th>
                <th className="py-2 px-3 text-left border-b">ຜູ້ໃຫ້ບໍລິການ</th>
                <th className="py-2 px-3 text-right border-b">ທຸລະກຳ</th>
                <th className="py-2 px-3 text-right border-b">ຍອດລວມ</th>
                <th className="py-2 px-3 text-center border-b">ຄ່າທຳນຽມ</th>
                <th className="py-2 px-3 text-center border-b">ສະຖານະ</th>
                <th className="py-2 px-3 text-left border-b">ໃຊ້ງານລ່າສຸດ</th>
                <th className="py-2 px-3 text-right border-b">ເຄື່ອງມື</th>
              </tr>
            </thead>
            <tbody>
              {isRefetching ? (
                <tr>
                  <td colSpan={10} className="py-4 text-center">
                    <div className="flex justify-center items-center">
                      <span>ກຳລັງໂຫລດຂໍ້ມູນ...</span>
                    </div>
                  </td>
                </tr>
              ) : filteredData && filteredData.length > 0 ? (
                filteredData.map((payment: any, i: number) => (
                  <tr key={payment._id}>
                    <td className="py-2 px-3 border-b">{i + 1}</td>
                    <td className="py-2 px-3 border-b">
                      <div className="flex flex-col gap-1">
                        <div className="font-bold text-[14px] text-primary">
                          {payment?.name || "--"}
                        </div>
                        <div className="text-[10px] text-neutral-500">
                          ID: {payment?._id}
                        </div>
                      </div>
                    </td>
                    <td className="py-2 px-3 border-b">
                      <Badge variant="info">
                        {getTypeLabel(payment?.type)}
                      </Badge>
                    </td>
                    <td className="py-2 px-3 border-b">
                      {payment?.provider || "--"}
                    </td>
                    <td className="py-2 px-3 border-b text-right">
                      <span className="font-semibold">
                        {formatCurrency(payment?.transactionCount || 0)}
                      </span>
                    </td>
                    <td className="py-2 px-3 border-b text-right">
                      <span className="font-bold text-success">
                        {formatCurrency(payment?.totalAmount || 0)}
                      </span>{" "}
                      ກີບ
                    </td>
                    <td className="py-2 px-3 border-b text-center">
                      <Badge variant="warning">
                        {payment?.feePercent || 0}%
                      </Badge>
                    </td>
                    <td className="py-2 px-3 border-b text-center">
                      {getStatusBadge(payment?.status)}
                    </td>
                    <td className="py-2 px-3 border-b">
                      <div className="text-[12px]">
                        {payment?.lastUsed || "ບໍ່ເຄີຍໃຊ້ງານ"}
                      </div>
                    </td>
                    <td className="py-2 px-3 border-b">
                      <div className="flex justify-end gap-1">
                        <button className="btn btn-outline-primary btn-sm flex items-center gap-1">
                          ແກ້ໄຂ
                        </button>
                        <button
                          className={`btn btn-sm flex items-center gap-1 ${
                            payment?.status === "active"
                              ? "btn-outline-danger"
                              : "btn-outline-success"
                          }`}
                        >
                          {payment?.status === "active" ? "ປິດ" : "ເປີດ"}
                        </button>
                        <button className="btn btn-outline-info btn-sm flex items-center gap-1">
                          ທົດສອບ
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={10} className="py-4 text-center">
                    ບໍ່ພົບຂໍ້ມູນ
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card>
      </div>

      <div className="mt-3">
        <Card>
          <Card.Header>ຂໍ້ມູນສຳຄັນ</Card.Header>
          <Card.Body>
            <div className="space-y-2">
              <div className="alert alert-info" role="alert">
                <strong>ຄຳແນະນຳ:</strong>{" "}
                ກະລຸນາກວດສອບການຕັ້ງຄ່າຕົວຊຳລະເງິນໃຫ້ຖືກຕ້ອງກ່ອນນຳໃຊ້ງານຈິງ
              </div>
              <div className="alert alert-warning" role="alert">
                <strong>ຄວາມປອດໄພ:</strong> ຢ່າແຊຣ໌ API Key ຫຼື Secret Key
                ກັບຜູ້ອື່ນ
              </div>
            </div>
          </Card.Body>
        </Card>
      </div>

      {/* popup for create/edit payment can be added here */}
      {/* <PopUpCreatePayment
        open={!!popup.PopUpCreatePayment}
        onClose={() => setPopup({})}
        callback={(data: any) => {
          console.log(data);
        }}
      /> */}

      <PopUpWithdrawRequest
        open={showWithdrawPopup}
        onClose={() => setShowWithdrawPopup(false)}
        callback={(data: any) => {
          console.log("withdraw request", data);
        }}
      />
    </div>
  );
}
